import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import {
  Package,
  Scan,
  Search,
  CheckCircle,
  AlertCircle,
  X,
  Plus,
  FileText,
  Smartphone,
  QrCode,
  Printer,
  ChevronRight,
  User,
  Trash2,
  Check,
  RefreshCw,
  Camera,
  Box,
  AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_BLUE = '#2b3380';

// Device Row Component
const DeviceRow = ({ device, isSelected, onToggle, isReceived }) => (
  <div 
    className={`flex items-center gap-3 p-4 rounded-xl border transition-all ${
      isReceived 
        ? 'bg-green-50 border-green-200' 
        : isSelected 
          ? 'bg-blue-50 border-blue-300' 
          : 'bg-white border-gray-100 hover:border-gray-200'
    }`}
  >
    {!isReceived && (
      <button
        onClick={() => onToggle(device.lead_id)}
        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
          isSelected 
            ? 'bg-[#2b3380] border-[#2b3380]' 
            : 'border-gray-300 hover:border-[#2b3380]'
        }`}
      >
        {isSelected && <Check className="w-4 h-4 text-white" />}
      </button>
    )}
    
    {isReceived && (
      <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
        <Check className="w-4 h-4 text-white" />
      </div>
    )}
    
    <div className="p-2 bg-gray-100 rounded-lg">
      <Smartphone className="w-5 h-5 text-[#2b3380]" />
    </div>
    
    <div className="flex-1 min-w-0">
      <p className="font-medium text-gray-800 text-sm">
        {device.device_brand} {device.device_model}
      </p>
      <p className="text-xs text-gray-500">{device.lead_id}</p>
      {device.imei && <p className="text-xs text-gray-400">IMEI: {device.imei}</p>}
    </div>
    
    <div className="text-right">
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
        device.pq_grade?.startsWith('Z1') || device.pq_grade?.startsWith('Z2')
          ? 'bg-green-100 text-green-700'
          : device.pq_grade?.startsWith('Z3') || device.pq_grade?.startsWith('Z4')
            ? 'bg-yellow-100 text-yellow-700'
            : 'bg-orange-100 text-orange-700'
      }`}>
        {device.pq_grade || 'N/A'}
      </span>
      <p className="text-sm font-semibold text-gray-800 mt-1">₹{(device.final_price || 0).toLocaleString()}</p>
    </div>
  </div>
);

// Scan Input Modal
const ScanModal = ({ isOpen, onClose, onScan, scannedIds }) => {
  const [inputValue, setInputValue] = useState('');
  
  if (!isOpen) return null;
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim()) {
      onScan(inputValue.trim());
      setInputValue('');
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center">
      <div className="bg-white w-full md:w-96 md:rounded-2xl rounded-t-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800">Scan Device</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="relative mb-4">
            <QrCode className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Scan or enter Lead ID / Pickup ID"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              autoFocus
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2b3380]/20 focus:border-[#2b3380]"
            />
          </div>
          
          <button
            type="submit"
            className="w-full py-3 bg-[#2b3380] text-white rounded-xl font-medium hover:bg-[#232a66] transition-colors"
          >
            Add Device
          </button>
        </form>
        
        {scannedIds.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-sm text-gray-500 mb-2">Scanned: {scannedIds.length}</p>
            <div className="flex flex-wrap gap-2">
              {scannedIds.slice(-5).map(id => (
                <span key={id} className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                  {id}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function GRNProcessing({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1); // 1: Select Manifest, 2: Receive Devices, 3: Generate DID
  const [manifests, setManifests] = useState([]);
  const [selectedManifest, setSelectedManifest] = useState(null);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [showScanModal, setShowScanModal] = useState(false);
  const [grnResult, setGrnResult] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [discrepancyNotes, setDiscrepancyNotes] = useState('');
  
  // Bin allocation state
  const [binAllocationResults, setBinAllocationResults] = useState([]);
  const [devicesNeedingBins, setDevicesNeedingBins] = useState([]);
  const [showBinCreationModal, setShowBinCreationModal] = useState(false);
  const [deviceForBinCreation, setDeviceForBinCreation] = useState(null);

  useEffect(() => {
    fetchPendingManifests();
    
    // Check for manifest ID in URL
    const manifestId = searchParams.get('manifest');
    if (manifestId) {
      fetchManifestDetails(manifestId);
    }
  }, [searchParams]);

  const fetchPendingManifests = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/warehouse/manifest/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setManifests(data.manifests || []);
      }
    } catch (error) {
      console.error('Error fetching manifests:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchManifestDetails = async (manifestId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/warehouse/manifest/${manifestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedManifest(data.manifest);
        setStep(2);
      }
    } catch (error) {
      console.error('Error fetching manifest:', error);
    }
  };

  const handleSelectManifest = (manifest) => {
    setSelectedManifest(manifest);
    setSelectedDevices([]);
    setStep(2);
  };

  const toggleDevice = (leadId) => {
    setSelectedDevices(prev => 
      prev.includes(leadId)
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const selectAllDevices = () => {
    const allIds = selectedManifest.leads?.map(l => l.lead_id) || [];
    setSelectedDevices(allIds);
  };

  const handleScan = (scannedId) => {
    // Find matching device
    const device = selectedManifest?.leads?.find(
      l => l.lead_id === scannedId || l.imei === scannedId
    );
    
    if (device && !selectedDevices.includes(device.lead_id)) {
      setSelectedDevices(prev => [...prev, device.lead_id]);
    }
  };

  const createGRN = async () => {
    if (selectedDevices.length === 0) {
      alert('Please select at least one device');
      return;
    }
    
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      
      // Create GRN
      const grnResponse = await fetch(`${API_URL}/api/warehouse/grn/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          manifest_id: selectedManifest.manifest_id,
          received_lead_ids: selectedDevices,
          receiver_id: user?.employee_id || user?.id || 'WH-USER',
          receiver_name: user?.name || 'Warehouse User',
          discrepancy_notes: discrepancyNotes || null
        })
      });
      
      if (grnResponse.ok) {
        const grnData = await grnResponse.json();
        
        // Generate DIDs for all received devices
        const didResponse = await fetch(
          `${API_URL}/api/warehouse/did/generate-bulk?grn_id=${grnData.grn.grn_id}`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        const didData = await didResponse.json();
        const generatedDIDs = didData.device_ids || [];
        
        // Automatic bin allocation for each device
        const binResults = [];
        const needBins = [];
        
        for (const leadId of selectedDevices) {
          const device = selectedManifest.leads?.find(l => l.lead_id === leadId);
          if (!device) continue;
          
          // Get the DID for this device (find from warehouse_devices collection)
          try {
            const binResponse = await fetch(`${API_URL}/api/warehouse/bin/auto-suggest`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
              },
              body: JSON.stringify({
                device_brand: device.device_brand || '',
                device_model: device.device_model || ''
              })
            });
            
            const binData = await binResponse.json();
            
            if (binData.bin_found) {
              binResults.push({
                lead_id: leadId,
                device_brand: device.device_brand,
                device_model: device.device_model,
                bin: binData.bin,
                allocated: true
              });
            } else {
              needBins.push({
                lead_id: leadId,
                device_brand: device.device_brand,
                device_model: device.device_model,
                suggested_new_bin: binData.suggested_new_bin,
                allocated: false
              });
            }
          } catch (binError) {
            console.error('Bin allocation error:', binError);
            needBins.push({
              lead_id: leadId,
              device_brand: device.device_brand,
              device_model: device.device_model,
              allocated: false
            });
          }
        }
        
        setBinAllocationResults(binResults);
        setDevicesNeedingBins(needBins);
        
        setGrnResult({
          grn: grnData.grn,
          dids: generatedDIDs
        });
        setStep(3);
      } else {
        const error = await grnResponse.json();
        alert(`Error: ${error.detail || 'Failed to create GRN'}`);
      }
    } catch (error) {
      console.error('Error creating GRN:', error);
      alert('Network error. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const missingDevices = selectedManifest?.leads?.filter(
    l => !selectedDevices.includes(l.lead_id)
  ) || [];

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">GRN Processing</h1>
            <p className="text-sm text-gray-500">Receive devices and generate GRN</p>
          </div>
          {step > 1 && step < 3 && (
            <button
              onClick={() => { setStep(1); setSelectedManifest(null); setSelectedDevices([]); }}
              className="text-sm text-gray-500 hover:text-[#2b3380]"
            >
              ← Back to Manifests
            </button>
          )}
        </div>

        {/* Progress Steps */}
        <div className="flex items-center gap-2 mb-6">
          {[
            { num: 1, label: 'Select Manifest' },
            { num: 2, label: 'Receive Devices' },
            { num: 3, label: 'GRN Complete' }
          ].map((s, idx) => (
            <React.Fragment key={s.num}>
              <div className={`flex items-center gap-2 ${step >= s.num ? 'text-[#2b3380]' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                  step > s.num ? 'bg-green-500 text-white' :
                  step === s.num ? 'bg-[#2b3380] text-white' : 'bg-gray-200'
                }`}>
                  {step > s.num ? <Check className="w-4 h-4" /> : s.num}
                </div>
                <span className="text-sm font-medium hidden md:block">{s.label}</span>
              </div>
              {idx < 2 && (
                <div className={`flex-1 h-0.5 ${step > s.num ? 'bg-green-500' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Select Manifest */}
        {step === 1 && (
          <div>
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2b3380]"></div>
              </div>
            ) : manifests.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
                <Package className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-800 mb-2">No Pending Manifests</h3>
                <p className="text-gray-500">All manifests have been received</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {manifests.map(manifest => (
                  <div
                    key={manifest.manifest_id}
                    onClick={() => handleSelectManifest(manifest)}
                    className="bg-white rounded-xl border border-gray-100 p-5 cursor-pointer hover:shadow-md hover:border-[#2b3380]/30 transition-all"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-800">{manifest.manifest_id}</h3>
                        <p className="text-sm text-gray-500">{manifest.pickup_date}</p>
                      </div>
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                        {manifest.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                      <User className="w-4 h-4" />
                      {manifest.engineer_name}
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-gray-800">{manifest.lead_count} devices</span>
                      <span className="text-sm font-semibold text-[#2b3380]">
                        ₹{((manifest.total_value || 0) / 1000).toFixed(1)}K
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Receive Devices */}
        {step === 2 && selectedManifest && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Device List */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                {/* Header */}
                <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-800">Devices in Manifest</h3>
                    <p className="text-sm text-gray-500">{selectedManifest.manifest_id}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={selectAllDevices}
                      className="px-3 py-1.5 text-sm font-medium text-[#2b3380] border border-[#2b3380] rounded-lg hover:bg-[#2b3380]/5"
                    >
                      Select All
                    </button>
                    <button
                      onClick={() => setShowScanModal(true)}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-[#2b3380] rounded-lg hover:bg-[#232a66] flex items-center gap-1"
                    >
                      <Scan className="w-4 h-4" />
                      Scan
                    </button>
                  </div>
                </div>
                
                {/* Device List */}
                <div className="p-4 space-y-3 max-h-[500px] overflow-y-auto">
                  {selectedManifest.leads?.map(device => (
                    <DeviceRow
                      key={device.lead_id}
                      device={device}
                      isSelected={selectedDevices.includes(device.lead_id)}
                      onToggle={toggleDevice}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="space-y-4">
              {/* Selection Summary */}
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <h3 className="font-semibold text-gray-800 mb-4">Receiving Summary</h3>
                
                <div className="space-y-3 mb-4">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Expected</span>
                    <span className="font-semibold text-gray-800">{selectedManifest.lead_count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Selected</span>
                    <span className="font-semibold text-green-600">{selectedDevices.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Missing</span>
                    <span className={`font-semibold ${missingDevices.length > 0 ? 'text-red-600' : 'text-gray-800'}`}>
                      {missingDevices.length}
                    </span>
                  </div>
                </div>
                
                {missingDevices.length > 0 && (
                  <div className="mb-4">
                    <label className="block text-sm text-gray-600 mb-2">Discrepancy Notes</label>
                    <textarea
                      value={discrepancyNotes}
                      onChange={(e) => setDiscrepancyNotes(e.target.value)}
                      placeholder="Reason for missing devices..."
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2b3380]/20"
                      rows={3}
                    />
                  </div>
                )}
                
                <button
                  onClick={createGRN}
                  disabled={selectedDevices.length === 0 || processing}
                  className="w-full py-3 bg-[#2b3380] text-white rounded-xl font-medium hover:bg-[#232a66] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {processing ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4" />
                      Create GRN & Generate DID
                    </>
                  )}
                </button>
              </div>

              {/* Missing Devices Warning */}
              {missingDevices.length > 0 && (
                <div className="bg-red-50 border border-red-100 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-red-700 mb-2">
                    <AlertCircle className="w-5 h-5" />
                    <span className="font-semibold">Missing Devices</span>
                  </div>
                  <div className="space-y-1">
                    {missingDevices.map(d => (
                      <p key={d.lead_id} className="text-sm text-red-600">
                        • {d.lead_id} - {d.device_brand} {d.device_model}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 3: GRN Complete */}
        {step === 3 && grnResult && (
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              
              <h2 className="text-2xl font-bold text-gray-800 mb-2">GRN Created Successfully!</h2>
              <p className="text-gray-500 mb-6">
                {grnResult.dids?.length || 0} Device IDs have been generated
              </p>
              
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <p className="text-sm text-gray-500 mb-1">GRN Number</p>
                <p className="text-xl font-bold text-[#2b3380]">{grnResult.grn?.grn_id}</p>
              </div>
              
              {/* Bin Allocation Summary */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-green-50 rounded-xl p-4 text-left">
                  <div className="flex items-center gap-2 mb-2">
                    <Box className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-green-800">Bins Allocated</span>
                  </div>
                  <p className="text-2xl font-bold text-green-600">{binAllocationResults.length}</p>
                  <p className="text-sm text-green-600">devices assigned to bins</p>
                </div>
                
                {devicesNeedingBins.length > 0 && (
                  <div className="bg-orange-50 rounded-xl p-4 text-left">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-5 h-5 text-orange-600" />
                      <span className="font-semibold text-orange-800">Needs Bin Assignment</span>
                    </div>
                    <p className="text-2xl font-bold text-orange-600">{devicesNeedingBins.length}</p>
                    <p className="text-sm text-orange-600">devices need new bins</p>
                  </div>
                )}
              </div>
              
              {/* Devices Needing Bins Warning */}
              {devicesNeedingBins.length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6 text-left">
                  <div className="flex items-center gap-2 text-orange-700 mb-3">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-semibold">Action Required: Create Bins</span>
                  </div>
                  <p className="text-sm text-orange-600 mb-3">
                    The following devices need bin assignment. Please create bins in Bin Master for these models:
                  </p>
                  <div className="space-y-2">
                    {devicesNeedingBins.map(d => (
                      <div key={d.lead_id} className="flex items-center justify-between p-2 bg-white rounded-lg border border-orange-200">
                        <span className="text-sm text-gray-700">
                          {d.device_brand} {d.device_model}
                        </span>
                        <button
                          onClick={() => navigate(`/warehouse/bin-master?model=${encodeURIComponent(d.device_model)}`)}
                          className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded hover:bg-orange-200"
                        >
                          Create Bin
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Allocated Bins */}
              {binAllocationResults.length > 0 && (
                <div className="text-left mb-6">
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <Box className="w-4 h-4" />
                    Bin Allocation Details
                  </h3>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {binAllocationResults.map(r => (
                      <div key={r.lead_id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <span className="text-sm text-gray-700">
                          {r.device_brand} {r.device_model}
                        </span>
                        <span className="text-sm font-mono text-green-700 bg-green-100 px-2 py-1 rounded">
                          {r.bin?.bin_id || 'N/A'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Generated DIDs */}
              <div className="text-left mb-6">
                <h3 className="font-semibold text-gray-800 mb-3">Generated Device IDs</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {grnResult.dids?.map(did => (
                    <div key={did} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="font-mono text-sm">{did}</span>
                      <button
                        onClick={() => navigate(`/warehouse/did-labels?did=${did}`)}
                        className="text-[#2b3380] hover:underline text-sm flex items-center gap-1"
                      >
                        <Printer className="w-4 h-4" />
                        Print
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/warehouse/did-labels')}
                  className="flex-1 py-3 text-[#2b3380] border border-[#2b3380] rounded-xl font-medium hover:bg-[#2b3380]/5 flex items-center justify-center gap-2"
                >
                  <Printer className="w-4 h-4" />
                  Print All Labels
                </button>
                <button
                  onClick={() => navigate('/warehouse/dashboard')}
                  className="flex-1 py-3 bg-[#2b3380] text-white rounded-xl font-medium hover:bg-[#232a66]"
                >
                  Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Scan Modal */}
        <ScanModal
          isOpen={showScanModal}
          onClose={() => setShowScanModal(false)}
          onScan={handleScan}
          scannedIds={selectedDevices}
        />
      </div>
    </Layout>
  );
}

export default GRNProcessing;
